// React
import { useNavigate } from 'react-router-dom';

// Components
import { Box, Button, IconButton, Paper, Typography, useMediaQuery } from '@mui/material';
import { ErrorOutline, HomeOutlined, DarkModeOutlined, LightModeOutlined } from '@mui/icons-material';
import CustomSnackbar from '../../custom/CustomSnackbar.js';

// Hooks
import { useAppTheme } from '../../../hooks/useAppTheme.js';

// Assets
import logo from '../../../assets/logo_SIMON_R4.png';
import banner from '../../../assets/banner_trans_SIMON.png';

function PasswordResetNotConfirmed(props) {
	const { setOpenFail } = props;
	const navigate = useNavigate();
	const mobile = useMediaQuery("(max-width:600px)");
	
	// Theme
	const { isDarkMode, toggleDarkMode, colors, darkTheme: activeDarkTheme, lightTheme: activeLightTheme } = useAppTheme();

	const goToLogin = () => {
		navigate('/');
	};

	return (
		<Box sx={{ 
			minHeight: '100vh',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			p: 2,
			background: colors.login.bg,
			backgroundSize: 'cover',
			backgroundPosition: 'center',
			backgroundRepeat: 'no-repeat',
			position: 'relative',
			overflow: 'hidden',
			transition: 'background 0.3s ease',
		}}>
			{/* Dark mode blur layer */}
			<Box sx={{
				position: 'absolute',
				top: '-100px', left: '-100px', right: '-100px', bottom: '-100px',
				background: activeDarkTheme.login.blur,
				filter: 'blur(40px)',
				backgroundBlendMode: 'screen, screen, normal',
				zIndex: 0,
				opacity: isDarkMode ? 1 : 0,
				visibility: isDarkMode ? 'visible' : 'hidden',
				transition: 'opacity 0.3s ease',
				pointerEvents: 'none',
			}} />
			
			{/* Light mode blur layer */}
			<Box sx={{
				position: 'absolute',
				top: '-100px', left: '-100px', right: '-100px', bottom: '-100px',
				background: activeLightTheme.login.blur,
				filter: 'blur(50px)',
				zIndex: 0,
				opacity: isDarkMode ? 0 : 1,
				visibility: isDarkMode ? 'hidden' : 'visible',
				transition: 'opacity 0.3s ease',
				pointerEvents: 'none',
			}} />

			{/* Noise overlay */}
			<Box sx={{
				position: 'absolute',
				top: 0, left: 0, right: 0, bottom: 0,
				pointerEvents: 'none',
				backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='${isDarkMode ? '0.03' : '0.02'}'/%3E%3C/svg%3E")`,
				zIndex: 1,
			}} />

			<CustomSnackbar 
				open={true} 
				onClose={() => setOpenFail(false)} 
				message="Link falsch oder abgelaufen!" 
				severity="error" 
				autoHideDuration={5000}
			/>

			{mobile ? (
				<Paper 
					sx={{ 
						mt: -16,
						width: '90%',
						maxWidth: '400px',
						minHeight: 'auto',
						borderRadius: 4,
						position: 'relative',
						zIndex: 1,
						backgroundColor: isDarkMode ? 'rgba(20, 20, 20, 0.5)' : 'rgba(255, 255, 255, 0.3)',
						border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.3)',
						boxShadow: isDarkMode ? '0 8px 32px 0 rgba(0, 0, 0, 0.37)' : '0 8px 32px 0 rgba(31, 38, 135, 0.15)',
						p: 3,
						transition: 'background-color 0.2s ease, border-color 0.2s ease',
					}}
				>
					<Box sx={{ 
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						gap: 2,
						minHeight: '100%',
					}}>
						{/* Dark Mode Toggle Button (Mobile) */}
						<IconButton
							onClick={toggleDarkMode}
							sx={{
								position: 'absolute',
								top: 16,
								right: 16,
								zIndex: 10,
								backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.6)' : 'rgba(0, 0, 0, 0.06)',
								color: isDarkMode ? colors.accentLight : 'text.secondary',
								border: '1px solid',
								borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
								boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
								'&:hover': {
									backgroundColor: isDarkMode ? 'rgba(30, 30, 30, 0.8)' : 'rgba(0, 0, 0, 0.1)',
									transform: 'scale(1.05)',
								},
								transition: 'background-color 0.2s ease, transform 0.2s ease',
							}}
						>
							{isDarkMode ? <LightModeOutlined /> : <DarkModeOutlined />}
						</IconButton>

						{/* Logo and Banner */}
						<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, width: '100%', mb: 1 }}>
							<img alt="logo" style={{ width: 90, height: 90 }} src={ logo }/>
							<img alt="banner" style={{ width: '80%', maxWidth: 280, filter: isDarkMode ? 'invert(1) hue-rotate(180deg) saturate(2)' : 'none' }} src={ banner }/>
						</Box>

						{/* Error Icon */}
						<ErrorOutline sx={{ fontSize: 60, color: 'error.main' }} />

						{/* Title */}
						<Typography variant="h6" fontWeight={600} sx={{ textAlign: 'center', width: '100%', color: isDarkMode ? 'white' : 'text.primary' }}>
							Link ungültig
						</Typography>
						
						<Typography variant="body2" sx={{ textAlign: 'center', width: '100%', mb: 1, color: isDarkMode ? 'white' : 'text.primary' }}>
							Der Passwort-Reset-Link ist falsch oder bereits abgelaufen. Bitte fordere einen neuen Link an.
						</Typography>

						{/* Back to Login Button */}
						<Button 
							variant="contained" 
							onClick={ goToLogin } 
							startIcon={ <HomeOutlined /> }
							fullWidth
							sx={{ 
								py: 1.2,
								color: 'white', 
								backgroundColor: colors.login.button.bg, 
								borderRadius: 2,
								textTransform: 'none',
								fontSize: '0.95rem',
								fontWeight: 600,
								boxShadow: colors.login.button.shadow,
								mt: 1,
								'&:hover': { 
									backgroundColor: colors.login.button.hover,
									boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
									transform: 'translateY(-1px)',
									transition: 'all 0.3s ease'
								},
								'&:active': {
									transform: 'translateY(0)',
								},
							}}
						>
							Zurück zum Login
						</Button>
					</Box>
				</Paper>
			) : (
				// Desktop View - Split Screen Design
				<Paper 
					sx={{ 
						width: '100%',
						maxWidth: '1000px',
						minHeight: '550px',
						maxHeight: '90vh',
						overflow: 'hidden',
						borderRadius: 7,
						display: 'flex',
						flexDirection: 'row',
						position: 'relative',
						zIndex: 1,
						backgroundColor: isDarkMode ? 'transparent' : 'transparent',
						backdropFilter: 'none',
						border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
						boxShadow: isDarkMode ? '0 20px 60px rgba(0, 0, 0, 0.5)' : '0 20px 60px rgba(0, 0, 0, 0.15)',
						transition: 'background-color 0.2s ease',
					}}
				>
					{/* Left Side - Banner and Logo */}
					<Box 
						sx={{ 
							width: '50%',
							background: isDarkMode ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
							p: 4,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							gap: 6,
							zIndex: 2,
							borderRight: isDarkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
							boxShadow: isDarkMode 
								? '8px 0 25px rgba(0, 0, 0, 0.4), inset -1px 0 0 rgba(255, 255, 255, 0.05)' 
								: '8px 0 30px rgba(0, 0, 0, 0.12), inset -1px 0 0 rgba(255, 255, 255, 0.5)',
							transition: 'background-color 0.2s ease, border-color 0.2s ease',
						}}
					>
						<img alt="banner" style={{ width: '90%', maxWidth: 400, filter: isDarkMode ? 'invert(1) hue-rotate(180deg) saturate(2)' : 'none' }} src={ banner }/>
						<img alt="logo" style={{ width: 200, height: 200 }} src={ logo }/>
					</Box>

					{/* Right Side - Error Message */}
					<Box 
						sx={{ 
							width: '50%',
							p: 4,
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							justifyContent: 'center',
							overflow: 'auto',
							gap: 3,
							backgroundColor: isDarkMode ? 'rgba(20, 20, 20, 0.4)' : 'rgba(255, 255, 255, 0.3)',
							border: isDarkMode ? 'none' : '1px solid rgba(20, 20, 20, 0.08)',
							borderLeft: 'none',
							borderRadius: isDarkMode ? 0 : '0 28px 28px 0',
							boxShadow: isDarkMode 
								? 'inset 3px 0 15px rgba(0, 0, 0, 0.3)' 
								: 'inset 3px 0 15px rgba(0, 0, 0, 0.10)',
							position: 'relative',
						}}
					>
						{/* Dark Mode Toggle Button (Desktop) */}
						<IconButton
							onClick={toggleDarkMode}
							sx={{
								position: 'absolute',
								top: 24,
								right: 24,
								zIndex: 10,
								backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)',
								color: isDarkMode ? colors.accentLight : 'text.secondary',
								border: '1px solid',
								borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
								boxShadow: 'none',
								'&:hover': {
									backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
									transform: 'scale(1.05)',
								},
								transition: 'background-color 0.2s ease, transform 0.2s ease',
							}}
						>
							{isDarkMode ? <LightModeOutlined /> : <DarkModeOutlined />}
						</IconButton>

						<ErrorOutline sx={{ fontSize: 80, color: 'error.main' }} />

						<Typography variant="h5" fontWeight={600} sx={{ textAlign: 'center', color: isDarkMode ? 'white' : 'text.primary' }}>
							Link ungültig
						</Typography>
						
						<Typography variant="body1" sx={{ textAlign: 'center', maxWidth: '80%', color: isDarkMode ? 'white' : 'text.primary' }}>
							Der Passwort-Reset-Link ist falsch oder bereits abgelaufen. Bitte fordere einen neuen Link an.
						</Typography>

						<Button 
							variant="contained" 
							onClick={ goToLogin } 
							startIcon={ <HomeOutlined /> }
							sx={{ 
								maxWidth: '75%',
								py: 1.3,
								px: 4,
								color: 'white', 
								backgroundColor: colors.login.button.bg, 
								borderRadius: 2,
								textTransform: 'none',
								fontSize: '1rem',
								fontWeight: 600,
								boxShadow: colors.login.button.shadow,
								'&:hover': { 
									backgroundColor: colors.login.button.hover,
									boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
									transform: 'translateY(-1px)',
									transition: 'all 0.3s ease'
								},
								'&:active': {
									transform: 'translateY(0)',
								},
							}}
						>
							Zurück zum Login
						</Button>
					</Box>
				</Paper>
			)}
		</Box>
	);
}

export default PasswordResetNotConfirmed;